import React from "react";
import { CheckCircle2, XCircle, Trophy, Clock3, Target, RotateCcw } from "lucide-react";

const fmtTime = (secs = 0) => {
  const total = Number(secs || 0);
  const mm = Math.floor(total / 60);
  const ss = Math.floor(total % 60);
  return `${mm}m ${ss}s`;
};

export default function AssessmentResultCard({
  title = "Resultado",
  subtitle = "Tu resultado final",
  resultado,
  primaryActionText = "Volver",
  onPrimaryAction,
  showCoins = true,
  extraContent = null,
}) {
  if (!resultado) return null;

  const porcentaje = Number(resultado.porcentaje ?? 0);
  const puntaje = Number(resultado.puntuacion_obtenida ?? 0);
  const puntajeMax = Number(resultado.puntuacion_maxima ?? 0);
  const correctas = Number(resultado.respuestas_correctas ?? 0);
  const incorrectas = Number(resultado.respuestas_incorrectas ?? 0);
  const preguntasTotales = Number(resultado.preguntas_totales ?? 0);
  const intento = Number(resultado.intento_ordinal ?? 1);
  const tiempo = Number(resultado.tiempo_empleado_segundos ?? 0);
  const aprobado = !!resultado.aprobado;
  const premio = Number(resultado.utmcoins_awarded ?? resultado.premio_utm ?? 0);
  const penaltyApplied = resultado.penalty_applied ?? null;

  return (
    <div className="arc-wrap">
      <div className={`arc-card ${aprobado ? "is-pass" : "is-fail"}`}>
        <div className="arc-top">
          <div className={`arc-statusIcon ${aprobado ? "ok" : "fail"}`}>
            {aprobado ? <CheckCircle2 size={28} /> : <XCircle size={28} />}
          </div>

          <div>
            <h2 className="arc-title">{title}</h2>
            <p className="arc-subtitle">{subtitle}</p>
          </div>
        </div>

        <div className="arc-scoreBlock">
          <div className="arc-scoreRing">
            <div className="arc-scoreValue">{porcentaje}%</div>
            <div className="arc-scoreLabel">Porcentaje</div>
          </div>

          <div className="arc-statusText">
            <span className={`arc-pill ${aprobado ? "ok" : "fail"}`}>
              {aprobado ? "Aprobado" : "No aprobado"}
            </span>

            <div className="arc-mainStat">
              {puntaje}
              {puntajeMax > 0 ? ` / ${puntajeMax}` : ""}
            </div>
            <div className="arc-mainStatLabel">Puntaje obtenido</div>
          </div>
        </div>

        <div className="arc-grid">
          <div className="arc-kpi">
            <Target size={17} />
            <div>
              <b>{puntajeMax > 0 ? `${puntaje}/${puntajeMax}` : puntaje}</b>
              <span>{puntajeMax > 0 ? "Nota obtenida" : "Puntaje"}</span>
            </div>
          </div>

          <div className="arc-kpi">
            <Clock3 size={17} />
            <div>
              <b>{fmtTime(tiempo)}</b>
              <span>Tiempo</span>
            </div>
          </div>

          <div className="arc-kpi">
            <RotateCcw size={17} />
            <div>
              <b>{intento}</b>
              <span>Intento</span>
            </div>
          </div>

          {Number.isFinite(correctas) && correctas >= 0 && Number.isFinite(preguntasTotales) && preguntasTotales > 0 ? (
            <div className="arc-kpi">
              <CheckCircle2 size={17} />
              <div>
                <b>{correctas}/{preguntasTotales}</b>
                <span>Correctas</span>
              </div>
            </div>
          ) : null}

          {Number.isFinite(incorrectas) && incorrectas > 0 ? (
            <div className="arc-kpi">
              <XCircle size={17} />
              <div>
                <b>{incorrectas}</b>
                <span>Incorrectas</span>
              </div>
            </div>
          ) : null}

          {showCoins && premio > 0 ? (
            <div className="arc-kpi arc-kpi-coins">
              <Trophy size={17} />
              <div>
                <b>+{premio}</b>
                <span>UTMCoins</span>
              </div>
            </div>
          ) : null}
        </div>

        {penaltyApplied ? (
          <div className="arc-warning">
            <b>Tu progreso del nivel fue ajustado.</b>
            <span>
              Se eliminó un {Number(penaltyApplied.penaltyPct ?? 100)}% del progreso
              porque no aprobaste el test dentro de los intentos configurados.
            </span>
          </div>
        ) : null}

        {extraContent ? <div className="arc-extra">{extraContent}</div> : null}

        <div className="arc-actions">
          <button className="arc-btn" onClick={onPrimaryAction}>
            {primaryActionText}
          </button>
        </div>
      </div>

      <style>{`
        .arc-wrap{
          display:flex;
          justify-content:center;
          padding:16px;
        }

        .arc-card{
          width:min(820px, 100%);
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:28px;
          box-shadow:0 24px 70px rgba(15,23,42,.10);
          padding:24px;
        }

        .arc-card.is-pass{
          background:
            radial-gradient(circle at top right, rgba(16,185,129,.10), transparent 28%),
            #fff;
        }

        .arc-card.is-fail{
          background:
            radial-gradient(circle at top right, rgba(239,68,68,.10), transparent 28%),
            #fff;
        }

        .arc-top{
          display:flex;
          align-items:center;
          gap:14px;
          margin-bottom:18px;
        }

        .arc-statusIcon{
          width:58px;
          height:58px;
          border-radius:18px;
          display:grid;
          place-items:center;
          flex:0 0 auto;
        }

        .arc-statusIcon.ok{
          background:#ecfdf5;
          color:#059669;
        }

        .arc-statusIcon.fail{
          background:#fef2f2;
          color:#dc2626;
        }

        .arc-title{
          margin:0;
          font-size:1.35rem;
          font-weight:950;
          color:#0f172a;
        }

        .arc-subtitle{
          margin:4px 0 0;
          color:#64748b;
          line-height:1.5;
        }

        .arc-scoreBlock{
          display:grid;
          grid-template-columns: 220px 1fr;
          gap:20px;
          align-items:center;
          margin:18px 0 20px;
        }

        .arc-scoreRing{
          border:1px solid #dbeafe;
          background:linear-gradient(180deg,#eff6ff,#f8fbff);
          border-radius:24px;
          padding:22px 18px;
          text-align:center;
        }

        .arc-scoreValue{
          font-size:3rem;
          line-height:1;
          font-weight:1000;
          color:#1d4ed8;
          letter-spacing:-.04em;
        }

        .arc-scoreLabel{
          margin-top:8px;
          color:#64748b;
          font-size:.92rem;
          font-weight:700;
        }

        .arc-statusText{
          display:flex;
          flex-direction:column;
          gap:10px;
          align-items:flex-start;
        }

        .arc-pill{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          min-height:34px;
          padding:0 12px;
          border-radius:999px;
          font-weight:900;
          font-size:.86rem;
        }

        .arc-pill.ok{
          background:#ecfdf5;
          color:#065f46;
          border:1px solid #a7f3d0;
        }

        .arc-pill.fail{
          background:#fff1f2;
          color:#991b1b;
          border:1px solid #fecaca;
        }

        .arc-mainStat{
          font-size:2rem;
          line-height:1;
          font-weight:950;
          color:#111827;
        }

        .arc-mainStatLabel{
          color:#64748b;
          font-weight:600;
        }

        .arc-grid{
          display:grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap:12px;
        }

        .arc-kpi{
          display:flex;
          align-items:flex-start;
          gap:10px;
          border:1px solid #e5e7eb;
          background:#fff;
          border-radius:18px;
          padding:14px;
        }

        .arc-kpi svg{
          color:#2563eb;
          flex:0 0 auto;
          margin-top:2px;
        }

        .arc-kpi b{
          display:block;
          font-size:1rem;
          color:#111827;
        }

        .arc-kpi span{
          display:block;
          margin-top:2px;
          color:#64748b;
          font-size:.86rem;
        }

        .arc-kpi-coins{
          background:linear-gradient(180deg,#fffbeb,#fff);
          border-color:#fde68a;
        }

        .arc-kpi-coins svg{
          color:#d97706;
        }

        .arc-extra{
          margin-top:16px;
        }

        .arc-warning{
          margin-top:16px;
          border:1px solid #fed7aa;
          background:linear-gradient(180deg,#fff7ed,#fffbeb);
          color:#9a3412;
          border-radius:18px;
          padding:14px 16px;
          display:grid;
          gap:4px;
        }

        .arc-warning b{
          font-size:.95rem;
        }

        .arc-warning span{
          font-size:.9rem;
          line-height:1.45;
        }

        .arc-actions{
          margin-top:18px;
          display:flex;
          justify-content:flex-end;
        }

        .arc-btn{
          min-width:180px;
          height:46px;
          border:none;
          border-radius:14px;
          background:#2563eb;
          color:#fff;
          font-weight:900;
          cursor:pointer;
          padding:0 18px;
          transition:.2s ease;
        }

        .arc-btn:hover{
          transform:translateY(-1px);
          box-shadow:0 14px 30px rgba(37,99,235,.22);
        }

        @media (max-width: 760px){
          .arc-scoreBlock{
            grid-template-columns:1fr;
          }

          .arc-grid{
            grid-template-columns:repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 520px){
          .arc-card{
            padding:18px;
            border-radius:22px;
          }

          .arc-top{
            align-items:flex-start;
          }

          .arc-scoreValue{
            font-size:2.4rem;
          }

          .arc-grid{
            grid-template-columns:1fr;
          }

          .arc-actions{
            justify-content:stretch;
          }

          .arc-btn{
            width:100%;
          }
        }
      `}</style>
    </div>
  );
}
